import { clients } from '@/lib/https';
import { API_BASES } from '@/constant/endpoint.constant';
import { AI_ENDPOINTS } from '@/modules/gpt-chats/constants/endpoint.constant';
import {
  IGetAgentByIdPayload,
  IGetAgentByIdResponse,
  IGetAgentModelResponse,
  IGetAgentsPayload,
  IGetAgentsResponse,
  IProcessFilesPayload,
  IAgentChatStreamPayload,
  ICreateAgentPayload,
  CreateAgentResponse,
  IUpdateAgentAIConfigurationPayload,
  IUpdateAgentAIConfigurationResponse,
  IPublishAgentPayload,
  IPublishAgentResponse,
  IQueryAgentPayload,
  IQueryAgentResponse,
  IUpdateAgentWidgetPayload,
  IUpdateAgentWidgetResponse,
  IUpdateAgentPersonaPayload,
  IUpdateAgentPersonaResponse,
  ISSEQueryAgentPayload,
  IDeleteAgentPayload,
  IDeleteAgentResponse,
  IWidgetColorSuggestionPayload,
  IWidgetColorSuggestionResponse,
  IAgentRetrievalTestingPayload,
  IAgentRetrievalTestingResponse,
} from '../types/agent.service.type';

export class AgentService {
  getAgents(payload: IGetAgentsPayload): Promise<IGetAgentsResponse> {
    return clients.post(`/blocksai-api/v1/agents/queries`, JSON.stringify(payload));
  }

  getAgentById(payload: IGetAgentByIdPayload): Promise<IGetAgentByIdResponse> {
    return clients.get(
      `/blocksai-api/v1/agents/query/${payload.id}?project_key=${payload.projectKey}`
    );
  }

  updateAgentPersona(payload: IUpdateAgentPersonaPayload): Promise<IUpdateAgentPersonaResponse> {
    return clients.post(
      `${API_BASES.AI}${AI_ENDPOINTS.AGENT_UPDATE_PERSONA}`,
      JSON.stringify(payload)
    );
  }

  updateAgentAIConfiguration(
    payload: IUpdateAgentAIConfigurationPayload
  ): Promise<IUpdateAgentAIConfigurationResponse> {
    return clients.post(
      `${API_BASES.AI}${AI_ENDPOINTS.AGENT_UPDATE_AI_CONFIG}`,
      JSON.stringify(payload)
    );
  }

  agentChatStream(widgetId: string, body: IAgentChatStreamPayload, sessionId?: string) {
    const queryParams = sessionId ? `?s_id=${sessionId}` : '';
    const url = `/blocksai-api/v1/ai-agent/chat/${widgetId}${queryParams}`;
    return clients.stream(url, JSON.stringify(body));
  }

  processFiles(payload: IProcessFilesPayload) {
    return clients.stream(`/blocksai-api/v1/ai-agent/file/process`, JSON.stringify(payload));
  }

  addAgent(payload: ICreateAgentPayload): Promise<CreateAgentResponse> {
    return clients.post(`/blocksai-api/v1/agents/create`, JSON.stringify(payload));
  }

  getllmModels(): Promise<IGetAgentModelResponse> {
    return clients.get(`${API_BASES.AI}${AI_ENDPOINTS.AGENT_MODELS}`);
  }

  query(payload: IQueryAgentPayload): Promise<IQueryAgentResponse> {
    return clients.post(`${API_BASES.AI}${AI_ENDPOINTS.AGENT_QUERY}`, JSON.stringify(payload));
  }

  publishAgents(payload: IPublishAgentPayload): Promise<IPublishAgentResponse> {
    return clients.get(
      `${API_BASES.AI}${AI_ENDPOINTS.AGENT_PUBLISH.replace(':id', payload.agent_id)}`
    );
  }

  updateAgentWidget(payload: IUpdateAgentWidgetPayload): Promise<IUpdateAgentWidgetResponse> {
    return clients.post(
      `${API_BASES.AI}${AI_ENDPOINTS.AGENT_UPDATE_WIDGET}`,
      JSON.stringify(payload)
    );
  }

  deleteAgentById(payload: IDeleteAgentPayload): Promise<IDeleteAgentResponse> {
    return clients.delete(`/blocksai-api/v1/agents/delete/${payload.agent_id}`);
  }

  async querySSE(payload: ISSEQueryAgentPayload): Promise<ReadableStream<Uint8Array>> {
    return clients.stream(
      `${API_BASES.AI}${AI_ENDPOINTS.AGENT_QUERY_STREAM}`,
      JSON.stringify(payload),
      {
        Accept: 'text/event-stream',
      }
    );
  }

  widgetColorSuggestion(
    payload: IWidgetColorSuggestionPayload
  ): Promise<IWidgetColorSuggestionResponse> {
    return clients.post(
      `${API_BASES.AI}${AI_ENDPOINTS.AGENT_WIDGET_COLOR_SUGGESTION}`,
      JSON.stringify(payload)
    );
  }
  retrievalTesting(
    agent_id: string,
    body: IAgentRetrievalTestingPayload
  ): Promise<IAgentRetrievalTestingResponse> {
    return clients.post(
      `${API_BASES.AI}${AI_ENDPOINTS.AGENT_RETRIEVAL_TEST.replace(':id', agent_id)}`,
      JSON.stringify(body)
    );
  }
}

export const agentService = new AgentService();
